// Turning "the site broke" into something a user can actually send us.
//
// A crash reaches the ErrorBoundary with an error and a component stack, but
// the interesting part is usually what the console printed just BEFORE it — the
// failed read, the warning, the path being fetched. Nothing was recorded, so
// every client report cost a full investigation. This module keeps a small ring
// buffer of console output and builds one pasteable text block out of it,
// which the error dialog copies to the clipboard.

const MAX_ENTRIES = 100
const MAX_ARGUMENT_LENGTH = 800
const CAPTURED_LEVELS = ['error', 'warn', 'log']

const entries = []
let originalMethods = null
let windowListeners = null

const clip = (text) => (text.length > MAX_ARGUMENT_LENGTH
  ? `${text.slice(0, MAX_ARGUMENT_LENGTH)}… [נחתך]`
  : text)

const formatArgument = (value) => {
  if (value instanceof Error) {
    return value.stack || `${value.name}: ${value.message}`
  }
  if (typeof value === 'string') {
    return value
  }
  if (value === null || value === undefined || typeof value !== 'object') {
    return String(value)
  }
  try {
    return JSON.stringify(value)
  } catch {
    // Circular structures, Firestore snapshots, DOM nodes…
    return String(value)
  }
}

/**
 * Record one console call. Exported for the console patch and for tests.
 * @param {string} level - 'error' | 'warn' | 'log'
 * @param {Array} args - the console arguments
 * @param {string} [at] - ISO timestamp; defaults to now
 */
export function recordConsoleEntry(level, args, at = new Date().toISOString()) {
  entries.push({
    level,
    at,
    text: clip(Array.from(args || []).map(formatArgument).join(' ')),
  })
  while (entries.length > MAX_ENTRIES) {
    entries.shift()
  }
}

/**
 * Start capturing console output. The original methods keep running, so
 * nothing changes for anyone watching devtools. Idempotent.
 * @param {Console} target
 * @return {function(): void} stop capturing (used by tests)
 */
export function installConsoleCapture(target = console) {
  if (originalMethods) {
    return uninstallConsoleCapture
  }

  originalMethods = { target, methods: {} }
  CAPTURED_LEVELS.forEach((level) => {
    const original = target[level]
    originalMethods.methods[level] = original
    target[level] = (...args) => {
      recordConsoleEntry(level, args)
      if (typeof original === 'function') {
        original.apply(target, args)
      }
    }
  })

  // A rejected promise never reaches an error boundary — the failed Firestore
  // read behind a wedged screen can surface here and nowhere else.
  if (typeof window !== 'undefined' && window.addEventListener) {
    windowListeners = {
      error: (event) => recordConsoleEntry('error', [
        'window error:', event?.error || event?.message, event?.filename, event?.lineno,
      ]),
      unhandledrejection: (event) => recordConsoleEntry('error', [
        'unhandled rejection:', event?.reason,
      ]),
    }
    Object.entries(windowListeners).forEach(([type, listener]) => {
      window.addEventListener(type, listener)
    })
  }

  return uninstallConsoleCapture
}

export function uninstallConsoleCapture() {
  if (windowListeners) {
    Object.entries(windowListeners).forEach(([type, listener]) => {
      window.removeEventListener(type, listener)
    })
    windowListeners = null
  }
  if (!originalMethods) {
    return
  }
  const { target, methods } = originalMethods
  Object.entries(methods).forEach(([level, original]) => {
    target[level] = original
  })
  originalMethods = null
}

export function capturedEntries() {
  return entries.slice()
}

export function clearCapturedEntries() {
  entries.length = 0
}

const errorText = (error) => {
  if (!error) {
    return 'לא ידועה'
  }
  return error.stack || `${error.name || 'Error'}: ${error.message ?? String(error)}`
}

/**
 * One pasteable block describing the crash: what broke, where, on what, and
 * what the console said on the way there.
 *
 * @param {Object} params
 * @param {Error} params.error
 * @param {{componentStack?: string}} [params.errorInfo]
 * @param {string} [params.at] - ISO timestamp of the crash
 * @param {Object} [params.environment] - overrides for tests
 * @return {string}
 */
export function buildErrorReport({ error, errorInfo, at, environment } = {}) {
  const env = {
    url: typeof window !== 'undefined' ? window.location?.href : '',
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
    online: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    ...environment,
  }
  const log = capturedEntries()

  return [
    'דיווח שגיאה — KravPayroll',
    `מועד: ${at || new Date().toISOString()}`,
    `כתובת: ${env.url}`,
    `דפדפן: ${env.userAgent}`,
    env.online === undefined ? null : `מחובר לרשת: ${env.online ? 'כן' : 'לא'}`,
    '',
    'שגיאה:',
    errorText(error),
    errorInfo?.componentStack ? `\nרכיבים:${errorInfo.componentStack}` : null,
    '',
    `יומן קונסול (${log.length} רשומות אחרונות):`,
    ...(log.length
      ? log.map(({ at: entryAt, level, text }) => `[${entryAt}] ${level}: ${text}`)
      : ['(ריק)']),
  ]
    .filter((line) => line !== null)
    .join('\n')
}

/**
 * Copy text to the clipboard. `navigator.clipboard` is unavailable on
 * http origins and in some in-app browsers, so fall back to a hidden textarea.
 * @return {Promise<boolean>} whether the copy succeeded
 */
export async function copyToClipboard(text) {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text)
      return true
    }
  } catch {
    // fall through to the legacy path
  }

  try {
    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', '')
    textarea.style.position = 'fixed'
    textarea.style.opacity = '0'
    document.body.appendChild(textarea)
    textarea.select()
    const copied = document.execCommand && document.execCommand('copy')
    document.body.removeChild(textarea)
    return Boolean(copied)
  } catch {
    return false
  }
}
